import { useCallback, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";

import { budgetText, globalText } from "../../../assets/texts";
import DeleteDialog from "../../../Components/DeleteDialog";
import { FilterTable } from "../../../Components/FilterTable/FilterTable";
import { FilterTableSkeleton } from "../../../Components/FilterTable/FilterTableSkeleton";
import FilterTableToolbarMeteredTitle from "../../../Components/FilterTable/Toolbar/FilterTableToolbarMeteredTitle";
import Hide from "../../../Components/HideChildren/Hide";
import { useBudgets } from "../../../Components/hooks/cloudAnalytics/budgets/useBudgets";
import useRouteMatchURL from "../../../Components/hooks/useRouteMatchURL";
import { cloudAnalytics } from "../../../constants/cypressIds";
import { useTierLimitReachedAnalyticsGovernanceBudgets } from "../../../Context/AnalyticsTierProvider";
import { useTier } from "../../../Context/TierProvider";
import { type Budget } from "../../../types";
import mixpanel from "../../../utils/mixpanel";
import { addAnalyticsWindowFunction } from "../../../utils/windowInit";
import { budgetsToAnalyticsResources } from "../analyticsResources/utils";
import ShareDialog from "../dialogs/shareDialog/ShareDialog";
import { useSelectionOperations } from "../hooks/useSelectionOperations";
import { EditLabelsMenuButton } from "../labels/components/EditLabelsMenuButton";
import EditPermissionsButton from "../labels/components/EditPermissionsButton";
import { useLabels } from "../labels/hooks";
import { updateColumnFiltersWithLabels } from "../labels/labelTableUtils";
import {
  type BudgetRowActions,
  BudgetTypes,
  BudgetTypesOptions,
  CloudAnalyticsEntities,
  timeIntervalOptions,
} from "../utilities";
import { filters, headerColumns } from "./BudgetBrowserColumns";
import { BudgetRow } from "./BudgetBrowserRow";
import { DEFAULT_BUDGET_AMOUNT } from "./const";
import { useCreateBudgetHandler, useDeleteBudgetHandler, useShareBudgetHandler } from "./hooks";
import { getBudgetAmountToDate } from "./utils";

const { budgets: budgetIds } = cloudAnalytics;

export const BudgetBrowser = () => {
  const { getFeatureLimit, getFeatureName } = useTier();
  const routeMatchURL = useRouteMatchURL();

  const [selected, setSelected] = useState<Budget[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [shareLoading, setShareLoading] = useState<boolean>(false);
  const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false);

  const analyticsGovernanceBudgets = useTierLimitReachedAnalyticsGovernanceBudgets();

  const [_allBudgets, rawBudget, budgetsLoading] = useBudgets();

  const { canDelete, canEdit } = useSelectionOperations(selected);

  const getBudgetPeriodValue = useCallback((budgetRow: Budget) => {
    if (budgetRow.data.config.type === BudgetTypes.RECURRING) {
      return (timeIntervalOptions ?? []).find((option) => option.value === budgetRow.data.config.timeInterval)
        ?.alternateLabel;
    }
    return BudgetTypesOptions.find((option) => option.value === BudgetTypes.FIXED)?.label;
  }, []);

  const budgets = useMemo(
    () =>
      rawBudget.map((b) => {
        const budgetAmount =
          b.data.config.amount && !isNaN(b.data.config.amount) ? b.data.config.amount : DEFAULT_BUDGET_AMOUNT;
        const budgetAmountToDate = getBudgetAmountToDate(
          budgetAmount,
          b.data.config.type,
          b.data.config.timeInterval,
          b.data.config.startPeriod,
          b.data.config.endPeriod
        );

        return {
          ...b,
          data: {
            ...b.data,
            config: {
              ...b.data.config,
              amount: budgetAmount,
            },
            varianceToDate: budgetAmountToDate - (b.data?.utilization?.current ?? 0),
            budgetAmountToDate,
            period: getBudgetPeriodValue(b),
            utilization: {
              ...b.data.utilization,
              current: b.data.utilization?.current ?? 0,
              forecasted: b.data.utilization?.forecasted ?? 0,
            },
          },
        };
      }),
    [rawBudget, getBudgetPeriodValue]
  );

  const [labels, labelsLoading] = useLabels();

  useEffect(() => {
    const updatedSelectedBudgets = budgets.filter((budget) => selected.some((x) => x.ref.id === budget.ref.id));

    setSelected(updatedSelectedBudgets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgets]);

  const handleNewBudget = useCreateBudgetHandler({
    baseUrl: routeMatchURL,
    mixpanelEventName: "analytics.budgets.new",
  });

  useEffect(() => {
    addAnalyticsWindowFunction({ handleNewBudget });
  }, [handleNewBudget]);

  const handleDeleteBudget = useDeleteBudgetHandler({
    selected,
    closeDialog: () => {
      setSelected([]);
      setOpenDeleteDialog(false);
    },
  });

  useEffect(() => {
    mixpanel.track("analytics.budgets.list");
  }, []);

  const BudgetRowWrapper = ({ row }: { row: Budget }) => {
    const data: Budget = {
      ...row,
      data: {
        ...row.data,
        period: getBudgetPeriodValue(row),
        currentPercentage:
          row.data.config.amount && row.data.utilization?.current
            ? (row.data.utilization?.current / (row.data.config.amount || 1)) * 100
            : 0,
      },
    };

    const actions: BudgetRowActions = {
      setOpenDeleteDialog,
      setSelected,
      setShareDialogOpen,
    };

    return (
      <BudgetRow
        actions={actions}
        budget={data}
        labels={labels}
        tierLimitReached={analyticsGovernanceBudgets?.limitReached ?? true}
      />
    );
  };

  const handleChangeSharing = useShareBudgetHandler({
    setLoading: setShareLoading,
    closeDialog: () => {
      setShareDialogOpen(false);
    },
    selected,
  });

  const toolbarTitle = useMemo(() => {
    const featureName = getFeatureName("governance:budgets") as string;
    const limit = getFeatureLimit("governance:budgets") as number | null;
    if (limit) {
      return (
        <FilterTableToolbarMeteredTitle
          value={analyticsGovernanceBudgets?.size ?? 0}
          limit={limit}
          featureName={featureName}
          loading={analyticsGovernanceBudgets.loading}
        />
      );
    }
    return featureName;
  }, [analyticsGovernanceBudgets, getFeatureLimit, getFeatureName]);

  const columnFiltersWithLabels = useMemo(() => updateColumnFiltersWithLabels(labels, filters, 9), [labels]);

  if (labelsLoading || budgetsLoading) {
    return (
      <Box
        sx={{
          p: 1,
        }}
      >
        <FilterTableSkeleton />
      </Box>
    );
  }

  if (!labels || !budgets) {
    return null;
  }

  return (
    <>
      <FilterTable<Budget>
        showRowsSelection={true}
        onRowsSelected={setSelected}
        tableItems={budgets}
        rowComponent={BudgetRowWrapper}
        headerColumns={headerColumns}
        filterColumns={columnFiltersWithLabels}
        filterBarPlaceholder={budgetText.FILTER_BUDGETS}
        persistenceKey="metric_tab"
        itemUniqIdentifierField="ref.id"
        defaultSortingColumnValue="data.timeModified"
        toolbarProps={{
          title: toolbarTitle,
          allowToEditColumns: true,
          deleteButton: {
            text: globalText.DELETE,
            onClick: () => {
              setOpenDeleteDialog(true);
            },
            disabled: !canDelete,
            tooltipTitle: selected.length === 0 || canDelete ? "" : budgetText.BUDGET_NOT_OWNER,
            "data-cy": budgetIds.browser.delete,
          },
          customSlot: (
            <>
              <Grid>
                <Hide mdDown>
                  <Button
                    disabled={analyticsGovernanceBudgets.limitReached ?? true}
                    variant="contained"
                    color="primary"
                    onClick={handleNewBudget}
                    data-cy={budgetIds.browser.newBudget}
                  >
                    {budgetText.CREATE_NEW_BUDGET}
                  </Button>
                </Hide>
              </Grid>
              <Grid>
                <EditLabelsMenuButton
                  labels={labels}
                  selectedResources={budgetsToAnalyticsResources(selected)}
                  disabled={!canEdit}
                />
              </Grid>
              <Grid>
                <EditPermissionsButton
                  selectedResources={budgetsToAnalyticsResources(selected)}
                  onClick={() => {
                    setShareDialogOpen(true);
                  }}
                  disabled={!canEdit}
                />
              </Grid>
            </>
          ),
        }}
      />
      {openDeleteDialog && (
        <DeleteDialog
          open={openDeleteDialog}
          title={budgetText.DELETE_SELECTED}
          message={budgetText.DELETE_MESSAGE}
          onDelete={handleDeleteBudget}
          onClose={() => {
            setSelected([]);
            setOpenDeleteDialog(false);
          }}
        />
      )}
      {shareDialogOpen && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
          }}
          entity={CloudAnalyticsEntities.BUDGET}
          onSharingChanged={handleChangeSharing}
          loading={shareLoading}
          shareEntities={selected.map((x) => x.data)}
        />
      )}
    </>
  );
};
